import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Mail, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 * Implements security best practices:
 * - Always shows success message (doesn't reveal if email exists)
 * - Rate limiting handled by backend
 *
 * Brand: Partner Portal uses Purple/Blue gradient theme
 * Colors: #282828 (signal-black), #4c1d95 (deep-purple), #1283EB (electric-blue)
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Request failed");
      }

      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      {/* Enhanced gradient background with electric blue accent */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1283EB]/15 via-[#4c1d95]/30 to-[#282828]" />
        <div className="absolute inset-0 bg-gradient-to-tl from-[#1283EB]/10 via-transparent to-transparent" />
      </div>

      <div className="max-w-md w-full space-y-8 p-10 bg-[#1a1a1a]/90 backdrop-blur-sm rounded-2xl border border-[#1283EB]/20 shadow-2xl mx-4">
        {/* Logo/Header */}
        <div className="text-center">
          <img
            src="/aiia-logo-white.svg"
            alt="aiia logo"
            className="h-14 mx-auto mb-6"
          />
          <h1 className="text-xl text-white font-semibold">
            Reset Your Password
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Enter your email address and we'll send you a password reset link
          </p>
        </div>

        {isSuccess ? (
          <div className="space-y-6">
            <Alert className="bg-emerald-950/50 border-emerald-800 text-emerald-200">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                Check your email for a password reset link. It may take a few minutes to arrive.
              </AlertDescription>
            </Alert>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                <Mail className="h-8 w-8 text-emerald-400" aria-hidden="true" />
              </div>
              <p className="text-sm text-white/60 mb-6">
                We've sent a reset link to <span className="font-medium text-white/80">{email}</span>
              </p>
              <p className="text-xs text-white/50 mb-6">
                The link will expire in 1 hour for security reasons.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full h-13 px-4 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                Return to login
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Back to Login Link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors focus:outline-none focus:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm">Back to login</span>
            </Link>

            {/* Error Alert with aria-live for screen readers */}
            <div
              role="alert"
              aria-live="polite"
              aria-atomic="true"
              className={error ? "" : "sr-only"}
            >
              {error && (
                <Alert variant="destructive" className="bg-red-950/50 border-red-800 text-red-200">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-white/80 mb-1.5"
                >
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  aria-required="true"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-12 bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/40 focus:border-[#1283EB] focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] transition-all"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-13 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1283EB] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" aria-hidden="true" />
                    Sending reset link...
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          </>
        )}

        {/* Footer */}
        <div className="text-center pt-4 border-t border-white/10">
          <p className="text-xs text-white/40">
            Need help?{" "}
            <a
              href="mailto:support@aiia.io"
              className="text-[#1283EB]/80 hover:text-[#1283EB] transition-colors"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
